import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Form, Input, InputNumber, message, Button } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import CommonSelect from "../../../components/Common/CommonSelect";
import {
  FeeType,
  FeeTypeCreatePayload,
  FeeTypeUpdatePayload,
} from "types/feeTypeTypes";

interface AddEditFeeTypeModalProps {
  feeData?: Partial<FeeType> | null;
  onSave: (
    feeData: FeeTypeCreatePayload | FeeTypeUpdatePayload
  ) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  classLevels: any[];
}

const AddEditFeeTypeModal: React.FC<AddEditFeeTypeModalProps> = ({
  feeData,
  onSave,
  onClose,
  isOpen,
  classLevels,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [pricingModel, setPricingModel] = useState<FeeType["pricingModel"]>(
    feeData?.pricingModel || "unique_payment"
  );
  const [selectedClassLevels, setSelectedClassLevels] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      console.log(feeData?.feePricing);
      form.setFieldsValue({
        name: feeData?.name || "",
        frequency: feeData?.frequency || "unique_payment",
        pricingModel: feeData?.pricingModel || "unique_payment",
        baseAmount: feeData?.baseAmount,
        levels: feeData?.feePricing?.map((p: any) => ({
          classLevelId: p.classLevelId,
          amount: p.amount
        })) || [],
      });
      setPricingModel(feeData?.pricingModel || "unique_payment");
      setSelectedClassLevels(feeData?.feePricing?.map((p: any) => p.classLevelId) || []);
    }
  }, [feeData, isOpen, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      if (values.pricingModel === "level_based_payment") {
        console.log(values.levels);
        values.levels = values.levels.map((level: any) => ({
          classLevelId: level.classLevelId?.value || level.classLevelId,
          amount: Number(level.amount)
        }));
      }

      values.baseAmount = pricingModel === "unique_payment" ? Number(values.baseAmount) : undefined;
      
      await onSave(values);
      message.success(
        feeData?.id
          ? t("fee_type_updated_successfully")
          : t("fee_type_added_successfully")
      );
      onClose();
      form.resetFields();
    } catch (error) {
      message.error(t("validation.please_fill_all_required_fields"));
    } finally {
      setLoading(false);
    }
  };

  const frequencyOptions = [
    { value: "unique_payment", label: t("unique_payment") },
    { value: "monthly_payment", label: t("monthly_payment") }
  ];

  const pricingModelOptions = [
    { value: "unique_payment", label: t("unique_payment") },
    { value: "level_based_payment", label: t("level_based_payment") }
  ];

  return (
    <CustomModal
      title={feeData?.id ? t("update_fee_type") : t("add_fee_type")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={feeData?.id ? t("save_changes") : t("add")}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("fee_name")}
          name="name"
          rules={[{ required: true, message: t("please_enter_name") }]}
        >
          <Input placeholder={t("fee_name")} />
        </Form.Item>

        <Form.Item
          label={t("frequency")}
          name="frequency"
          rules={[
            {
              required: true,
              message: t("validation.please_select_frequency"),
            },
          ]}
        >
          <CommonSelect 
            options={frequencyOptions}
            defaultValue={frequencyOptions.find(option => option.value === feeData?.frequency)}
            onChange={(value: any) => {
              console.log(value);
              form.setFieldValue('frequency', value?.value || "")
            }}
          />
        </Form.Item>

        <Form.Item
          label={t("pricing_model")}
          name="pricingModel"

          rules={[
            {
              required: true,
              message: t("validation.please_select_pricing_model"),
            },
          ]}
        >
          <CommonSelect 
            options={pricingModelOptions}
            defaultValue={pricingModelOptions.find(option => option.value === pricingModel)}
            onChange={(value: any) => {
                form.setFieldValue('pricingModel', value?.value || "")
               setPricingModel(value?.value || "")
            }
             }
          />
        </Form.Item>

        {pricingModel === "unique_payment" ? (
          <Form.Item
            label={t("amount")}
            name="baseAmount"
            rules={[
              { required: true, message: t("validation.please_enter_amount") },
            ]}
          >
            <InputNumber
              className="w-100"
              placeholder={t("amount")}
              min={0}
              precision={2}
              addonAfter="MAD"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            />
          </Form.Item>
        ) : (
          <Form.List name="levels">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div
                    key={field.key}
                    style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                  >
                    <Form.Item
                      {...field}
                      label={index === 0 ? t("class_level") : ""}
                      name={[field.name, "classLevelId"]}
                      rules={[
                        {
                          required: true,
                          message: t("please_select_a_class_level"),
                        },
                      ]}
                      style={{ flex: 1 }}
                    >
                      <CommonSelect 
                        className="select"
                        options={classLevels
                          .filter(cl => !selectedClassLevels.includes(cl.id) || 
                            form.getFieldValue(['levels', field.name, 'classLevelId']) === cl.id)
                          .map(cl => ({
                            value: cl.id,
                            label: cl.name
                          }))}
                        defaultValue={classLevels.find(cl => cl.id === form.getFieldValue(['levels', field.name, 'classLevelId']))?.id ? {
                          value: form.getFieldValue(['levels', field.name, 'classLevelId']),
                          label: classLevels.find(cl => cl.id === form.getFieldValue(['levels', field.name, 'classLevelId']))?.name
                        } : undefined}
                        onChange={(value) => {
                          const newSelectedLevels = [...selectedClassLevels];
                          const oldValue = form.getFieldValue(['levels', field.name, 'classLevelId']);
                          const valueIndex = newSelectedLevels.indexOf(oldValue);
                          if (valueIndex > -1) {
                            newSelectedLevels.splice(valueIndex, 1);
                          }
                          newSelectedLevels.push(value?.value || "");
                          setSelectedClassLevels(newSelectedLevels);
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label={index === 0 ? t("amount") : ""}
                      name={[field.name, "amount"]}
                      rules={[
                        {
                          required: true,
                          message: t("please_enter_an_amount"),
                        },
                      ]}
                      style={{ flex: 1 }}
                    >
                      <InputNumber
                        className="w-100"
                        placeholder={t("amount")}
                        min={0}
                        precision={2}
                        addonAfter="MAD"
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        type="link"
                        danger
                        style={{ marginTop: index === 0 ? "32px" : 0 }}
                        onClick={() => {
                          const removedValue = form.getFieldValue(['levels', field.name, 'classLevelId']);
                          setSelectedClassLevels(selectedClassLevels.filter(id => id !== removedValue));
                          remove(field.name);
                        }}
                      >
                        <i className="ti ti-trash" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  <i className="ti ti-plus me-1" />
                  {t("add_class_level")}
                </Button>
              </>
            )}
          </Form.List>
        )}
      </Form>
    </CustomModal>
  );
};

export default AddEditFeeTypeModal;
